import React, { useCallback, useRef, useState } from 'react';

import { Button } from '@/design-system/atoms/Button';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { IconList } from '@/design-system/atoms/Icons/IconList';
import { IconMap } from '@/design-system/atoms/Icons/IconMap';
import { IconSliders } from '@/design-system/atoms/Icons/IconSliders';
import { Theme } from '@/design-system/atoms/Theme';
import { ContentCarousel } from '@/design-system/molecules/ContentCarousel';
import * as ContentCarouselStories from '@/design-system/molecules/ContentCarousel/ContentCarousel.stories';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import classNames from '@/utils/classnames';

import { useLocationFilterContext } from '../../contexts/LocationFilterContext';
import { useLocationSearchContext } from '../../contexts/LocationSearchContext';
import { useLocationStateContext } from '../../contexts/LocationStateContext';
import { useLocationUIContext } from '../../contexts/LocationUIContext';
import { useLocationViewState } from '../../hooks/useLocationViewState';
import { LocationFilter } from '../LocationFilter';
import { LocationResultsList } from '../LocationResultsList';
import { LocationToolbar } from '../LocationToolbar';
import styles from './LocationMain.module.scss';

const LocationMain = ({ analytics, ...props }) => {
    const state = useLocationStateContext();

    const { filterLabel, filterAriaLabel, smallBreakpoint } = useLocationUIContext();
    const { handleShowResultList, handleShowMaps } = useLocationSearchContext();
    const viewState = useLocationViewState();
    const breakpoints = useBreakpoint();
    const isMobile = !breakpoints?.large;
    const locationMainRef = useRef();
    const scrollFooterRef = useRef();
    const { openPanel, setOpenPanel } = useLocationFilterContext();
    const [footerVisible, setFooterVisible] = useState(false);
    const [notInert, setNotInert] = useState(false);
    const [totalResults, setTotalResults] = React.useState(null);

    const {
        coveo: {
            controller: { querySummary },
        },
    } = useLocationSearchContext();

    const filterPanelOpen = React.useCallback(() => {
        setOpenPanel(true);
    }, [setOpenPanel]);

    const handleScroll = useCallback(() => {
        const mainTop = locationMainRef.current?.getBoundingClientRect().top;
        const mainBottom = locationMainRef.current?.getBoundingClientRect().bottom;
        const currentScrollY = window.scrollY;
        const scrollFooterStyles =
            scrollFooterRef?.current?.currentStyle ||
            (scrollFooterRef?.current && window?.getComputedStyle(scrollFooterRef?.current));
        if (currentScrollY > mainTop && mainBottom >= window?.innerHeight) {
            //while scrolling results control & sticky footer
            setFooterVisible(true);
            setNotInert(true);
        } else if (scrollFooterStyles?.position !== 'sticky' && currentScrollY > mainTop) {
            //Non sticky footer allow filters to be triggered
            setFooterVisible(false);
            setNotInert(true);
        } else {
            setFooterVisible(false);
            setNotInert(false);
        }
    }, [setFooterVisible, setNotInert]);

    React.useEffect(() => {
        return querySummary.subscribe(() => {
            const { total } = querySummary.state;

            setTotalResults(total);
        });
    }, [querySummary]);

    React.useEffect(() => {
        if (isMobile) {
            window.addEventListener('scroll', handleScroll, { passive: true });

            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        }
    }, [handleScroll, isMobile]);

    React.useEffect(() => {
        if (openPanel && isMobile) {
            document.body.style.overflow = 'hidden';
        } else {
            setOpenPanel(false);
            document.body.style.overflow = 'auto';
        }
    }, [openPanel, isMobile, setOpenPanel]);

    if (state === 'default') {
        return null;
    }

    return (
        <div
            className={styles['location-search__main']}
            {...(openPanel ? { inert: '' } : '')}
            ref={locationMainRef}
        >
            {state === 'results' ? (
                <>
                    <ContentContainer>
                        <div className={styles['location-search__tools']}>
                            <LocationToolbar />
                        </div>
                        <Grid>
                            <GridColumn
                                className={styles['location-search__filters']}
                                colSpan={{ sm: 4, md: 6, lg: 3 }}
                                colStart={{ sm: 1, lg: 1 }}
                            >
                                {!isMobile && <LocationFilter />}
                            </GridColumn>

                            <GridColumn
                                className={styles['location-search__views']}
                                colSpan={{ sm: 4, md: 6, lg: 9 }}
                                colStart={{ sm: 1, lg: 4 }}
                            >
                                <LocationResultsList analytics={analytics} />
                            </GridColumn>
                        </Grid>
                    </ContentContainer>
                    {isMobile && state === 'results' && totalResults > 0 && (
                        <div
                            ref={scrollFooterRef}
                            className={classNames(
                                styles[`location-search__scroll-footer`],
                                footerVisible && styles[`location-search__scroll-footer--visible`],
                            )}
                            {...(openPanel || !notInert ? { inert: '' } : {})}
                        >
                            <Button
                                label={
                                    viewState === 'result-list'
                                        ? smallBreakpoint?.mapView?.label
                                        : smallBreakpoint?.listView?.label
                                }
                                ariaLabel={
                                    viewState === 'result-list'
                                        ? smallBreakpoint?.mapView?.ariaLabel
                                        : smallBreakpoint?.listView?.ariaLabel
                                }
                                buttonStyle={Button.STYLE.SECONDARY}
                                fullWidth={{ md: true }}
                                iconComponent={viewState === 'result-list' ? IconMap : IconList}
                                onClick={() => {
                                    viewState === 'result-list' && handleShowMaps();
                                    viewState === 'maps' && handleShowResultList();
                                }}
                            />
                            <Button
                                label={filterLabel || 'Filter'}
                                ariaLabel={filterAriaLabel || 'Filter'}
                                buttonStyle={Button.STYLE.SECONDARY}
                                iconComponent={IconSliders}
                                fullWidth={{ md: true }}
                                onClick={filterPanelOpen}
                            />
                        </div>
                    )}
                </>
            ) : (
                <ContentCarousel
                    {...ContentCarouselStories.default.args}
                    theme={Theme.NAME.DEFAULT}
                />
            )}
        </div>
    );
};

LocationMain.propTypes = {};

export default LocationMain;
